import { useContext } from "react"
import { Div, Text } from "atomize"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons"
import { theme } from "../settings/config"
import { profileContext } from "../providers/profile.provider"
import { schoolContext } from "../providers/school.provider"
import { isMatch } from "../utils/match.utils"
import {
	formatHoursSpoken,
	formatMinutes,
	lessonEndMinutes,
	lessonPeopleInLine,
	lessonStartMinutes,
	overlapsWithin,
} from "../utils/week.utils"
import useElsewhereOnDate from "./useElsewhereOnDate"
import useCoachLessonsOnDate from "./useCoachLessonsOnDate"
import useCourtLessonsOnDate from "./useCourtLessonsOnDate"
import { useSchoolName } from "./ElsewhereLessonRow"

const minutesOf = time => {
	const [hours, minutes] = (time ?? "").split(":").map(Number)
	return Number.isFinite(hours) && Number.isFinite(minutes) ? hours * 60 + minutes : null
}

const fromTo = lesson =>
	`Dalle ${formatMinutes(lessonStartMinutes(lesson))} alle ${formatMinutes(lessonEndMinutes(lesson))}`

// Una lezione che si sta segnando a un'ora già presa. Non si blocca niente —
// magari quella di là salta, o è un cambio campo voluto — ma lo si dice
// prima di salvare, con cosa c'è e per quanto. Tre cose diverse, in un
// riquadro solo:
//
// - il maestro ha già una lezione qui, o gioca lui (#242)
// - il campo a quell'ora è di un'altra lezione, anche di un collega (#242)
// - il maestro è in un altro circolo, per chi ha scelto di vederli; solo per
//   le proprie lezioni, perché gli altri circoli sono i propri
//
// `court` è null quando la lezione un campo non ce l'ha.
function LessonOverlapWarning({ date, startTime, endTime, lessonCoach, court, lessonID }) {
	const { coach } = useContext(profileContext)
	const { getSelectedSchool } = useContext(schoolContext)
	const schoolName = useSchoolName()

	const isMine = lessonCoach?.coachID === coach?.coachID
	const coachLessons = useCoachLessonsOnDate(lessonCoach?.coachID, date)
	const courtLessons = useCourtLessonsOnDate(date, court)
	const elsewhere = useElsewhereOnDate(isMine ? date : null)

	const from = minutesOf(startTime)
	const to = minutesOf(endTime)
	if (from === null || to === null || to <= from) return null

	const busy = overlapsWithin(coachLessons, from, to, lessonID)
	const busyIDs = new Set(busy.map(({ lesson }) => lesson.lessonID))
	// una lezione del maestro sullo stesso campo si dice una volta, sopra
	const courtTaken = overlapsWithin(courtLessons, from, to, lessonID).filter(
		({ lesson }) => !busyIDs.has(lesson.lessonID),
	)
	const away = isMine ? overlapsWithin(elsewhere, from, to) : []
	if (busy.length + courtTaken.length + away.length === 0) return null

	// I maestri del circolo, una volta caricati: prima sono reference.
	const coachNameOf = coachID => {
		const schoolCoaches = getSelectedSchool()?.coaches
		return Array.isArray(schoolCoaches) ? null : (schoolCoaches?.[coachID]?.coachName ?? null)
	}
	const lessonCoachName = lessonCoach?.coachName ?? coachNameOf(lessonCoach?.coachID)

	const busyLine = lesson => {
		const people = lessonPeopleInLine(lesson)
		if (isMatch(lesson))
			return isMine ? <>giochi tu</> : <>gioca {lessonCoachName ?? "il maestro"}</>
		return (
			<>
				{isMine ? "hai già" : `${lessonCoachName ?? "il maestro"} ha già`} una lezione
				{people && (
					<>
						{" "}
						con <b>{people}</b>
					</>
				)}
			</>
		)
	}

	const courtLine = lesson => {
		const courtName = <b>campo {court.courtNumber}</b>
		const people = lessonPeopleInLine(lesson)
		const isOwn = lesson.coachID === coach?.coachID
		const owner = isOwn ? null : coachNameOf(lesson.coachID)
		if (isMatch(lesson))
			return isOwn ? (
				<>sul {courtName} giochi tu</>
			) : (
				<>
					sul {courtName} gioca <b>{owner ?? "un collega"}</b>
				</>
			)
		return (
			<>
				il {courtName} è {isOwn ? "tuo" : owner ? <>di <b>{owner}</b></> : "occupato"}
				{people && `, con ${people}`}
			</>
		)
	}

	return (
		<Div
			role="alert"
			d="flex"
			align="flex-start"
			m={{ b: "0.75rem" }}
			p="0.6rem 0.75rem"
			rounded="10px"
			bg={theme.colors.primary_light}
			style={{ gap: "0.6rem" }}>
			<FontAwesomeIcon
				icon={faTriangleExclamation}
				color={theme.colors.warning}
				style={{ marginTop: "0.2rem" }}
			/>
			<Div d="flex" flexDir="column" style={{ gap: "0.3rem" }}>
				{busy.map(({ lesson, minutes }) => (
					<Text key={lesson.lessonID} textSize="caption" textColor={theme.colors.gunmetal}>
						{fromTo(lesson)} {busyLine(lesson)}: questa si sovrappone per{" "}
						{formatHoursSpoken(minutes)}.
					</Text>
				))}
				{away.map(({ lesson, minutes }) => (
					<Text key={lesson.lessonID} textSize="caption" textColor={theme.colors.gunmetal}>
						{fromTo(lesson)} sei al <b>{schoolName(lesson.schoolID)}</b>: questa lezione si
						sovrappone per {formatHoursSpoken(minutes)}.
					</Text>
				))}
				{courtTaken.map(({ lesson, minutes }) => (
					<Text key={lesson.lessonID} textSize="caption" textColor={theme.colors.gunmetal}>
						{fromTo(lesson)} {courtLine(lesson)}: si sovrappone per{" "}
						{formatHoursSpoken(minutes)}.
					</Text>
				))}
				<Text textSize="caption" textColor={theme.colors.text_light}>
					Puoi salvarla lo stesso.
				</Text>
			</Div>
		</Div>
	)
}

export default LessonOverlapWarning
